import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import axiosInstance from "../../../axiosInstance";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";

const audienceOptions = [
  { value: "all", label: "All Users" },
  { value: "recruiters", label: "Recruiters" },
  { value: "job_seekers", label: "Job Seekers" },
];

const initialForm = {
  audience: "all",
  title: "",
  body: "",
  actionUrl: "",
  imageUrl: "",
  dataKey: "",
  dataValue: "",
};

const NotificationCenter = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const customData = useMemo(() => {
    if (!form.dataKey.trim()) return {};
    return { [form.dataKey.trim()]: form.dataValue.trim() };
  }, [form.dataKey, form.dataValue]);

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const submitNotification = async (dryRun = false) => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and message are required");
      return;
    }

    try {
      setSending(true);
      const authConfig = await getJobPortalAuthConfig();
      const response = await axiosInstance.post(
        "/job-portal/admin/notifications/send",
        {
          audience: form.audience,
          title: form.title.trim(),
          body: form.body.trim(),
          actionUrl: form.actionUrl.trim(),
          imageUrl: form.imageUrl.trim(),
          data: customData,
          dryRun,
        },
        authConfig
      );

      const result = response.data?.data || {};
      setPreview(result);
      toast.success(
        dryRun
          ? `Preview: ${result.tokens || 0} devices`
          : `Sent to ${result.successCount || 0} devices`
      );
      if (!dryRun) setForm(initialForm);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to send notification");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Notification Center</h4>
              <p className="text-muted small mb-0">
                Send custom push notifications to all users, recruiters, or job seekers.
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-xl-7">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Audience</label>
                      <select
                        className="form-select"
                        value={form.audience}
                        onChange={updateForm("audience")}
                      >
                        {audienceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Open Link</label>
                      <input
                        className="form-control"
                        placeholder="/jobs or https://..."
                        value={form.actionUrl}
                        onChange={updateForm("actionUrl")}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Title</label>
                      <input
                        className="form-control"
                        value={form.title}
                        onChange={updateForm("title")}
                        maxLength={80}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Message</label>
                      <textarea
                        className="form-control"
                        rows={5}
                        value={form.body}
                        onChange={updateForm("body")}
                        maxLength={240}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Image URL</label>
                      <input
                        className="form-control"
                        value={form.imageUrl}
                        onChange={updateForm("imageUrl")}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Custom Data Key</label>
                      <input
                        className="form-control"
                        placeholder="campaign"
                        value={form.dataKey}
                        onChange={updateForm("dataKey")}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Custom Data Value</label>
                      <input
                        className="form-control"
                        placeholder="summer_hiring"
                        value={form.dataValue}
                        onChange={updateForm("dataValue")}
                      />
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={sending}
                      onClick={() => submitNotification(true)}
                    >
                      Preview Audience
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={sending}
                      onClick={() => submitNotification(false)}
                    >
                      {sending ? "Sending..." : "Send Notification"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-5">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="mb-3">Preview</h6>
                  <div className="border rounded p-3 bg-light">
                    <div className="fw-semibold">{form.title || "Notification title"}</div>
                    <div className="text-muted small mt-1">
                      {form.body || "Message body will appear here."}
                    </div>
                    {form.actionUrl && (
                      <div className="small text-primary mt-2">{form.actionUrl}</div>
                    )}
                  </div>
                  {preview && (
                    <div className="mt-3 small text-muted">
                      Matched users: {preview.users || 0}. Devices:{" "}
                      {preview.tokens || 0}. Success: {preview.successCount || 0}.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default NotificationCenter;

